import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWorkspaceStore } from '@/lib/stores';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, XCircle, Star, GitCompare, Code2, BarChart3, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

export default function ModelDashboard({ sessionId }) {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [compareModels, setCompareModels] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadModels();
  }, [sessionId]);

  const loadModels = async () => {
    try {
      const res = await axios.get(`${API_BASE}/agents/state/${sessionId}`);
      const state = res.data.state;
      if (state?.model_runs_count > 0) {
        // Get full state for model runs (stored in pharm_state_store)
        // For now, use chat history to find model results
      }
    } catch (e) { console.error(e); }
    
    // Check workspace store for models from chat
    const wsModels = useWorkspaceStore.getState().artifacts?.filter(a =>
      a.type === 'model_summary' || a.type === 'parameter_table'
    ) || [];
    setModels(wsModels);
    setLoading(false);
  };

  const toggleCompare = (model) => {
    setCompareModels(prev => {
      if (prev.find(m => m.id === model.id)) {
        return prev.filter(m => m.id !== model.id);
      }
      return [...prev, model].slice(-3); // Max 3 for comparison
    });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Model Development</h2>
        <div className="flex gap-2">
          {compareModels.length >= 2 && (
            <Button size="sm" onClick={() => setShowCompare(true)} className="rounded-full h-8 text-xs px-3">
              <GitCompare className="w-3.5 h-3.5 mr-1" /> Compare ({compareModels.length})
            </Button>
          )}
        </div>
      </div>

      {models.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center py-16">
            <BarChart3 className="w-10 h-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No model runs yet</p>
            <p className="text-xs text-muted-foreground/50 mt-1">Ask "Build a PopPK model" in the chat to start modeling</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {models.map((model, i) => (
            <motion.div key={model.id || i}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}>
              <Card className={`card-hover cursor-pointer ${compareModels.find(m => m.id === model.id) ? 'border-primary' : ''}`}
                onClick={() => { setSelectedModel(model); setShowDetail(true); }}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                          {model.name || model.title || 'Model Run'}
                        </span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                          {model.type || 'model'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {model.agent && <span>{model.agent}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); toggleCompare(model); }}>
                        <GitCompare className="w-3 h-3 mr-1" /> {compareModels.find(m => m.id === model.id) ? 'Selected' : 'Compare'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty state guidance */}
      <div className="mt-8 p-4 rounded-xl border border-dashed border-border/50">
        <h3 className="text-sm font-medium mb-2" style={{ fontFamily: 'Space Grotesk' }}>Getting Started</h3>
        <p className="text-xs text-muted-foreground">In the Chat tab, try:</p>
        <ul className="text-xs text-muted-foreground mt-1 space-y-1 ml-4 list-disc">
          <li>"Build a PopPK model" — fits 1-CMT and 2-CMT, shows comparison</li>
          <li>"Run covariate analysis" — screens covariates with p-values</li>
          <li>"Fit an Emax PD model" — links PK to PD endpoint</li>
          <li>"Run exposure-response analysis" — logistic E-R with curve</li>
        </ul>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              {selectedModel?.name || selectedModel?.title || 'Model Details'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{selectedModel?.type}</p>
            <pre className="text-[10px] font-mono bg-muted/20 rounded-lg p-4 overflow-auto max-h-[300px]">
              {JSON.stringify(selectedModel, null, 2)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Model Comparison</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Model</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {compareModels.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-medium">{m.name || m.title}</TableCell>
                    <TableCell className="text-xs font-mono">{m.type}</TableCell>
                    <TableCell className="text-xs">{m.agent}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
